import type { CanvasGroup, CanvasStoreContext } from '../types';
import type { CanvasStore } from '../stores/canvasStore';
import type { GroupData } from '../entityTypes';
import { getHandler } from '../entities/registry';
import { buildGroupBoundsMap } from './buildGroupBoundsMap';

/**
 * After dragging entities, add ungrouped entities to the smallest group they landed in.
 *
 * @param store - The canvas store (used for dispatching group.setMembers events).
 * @param draggedNodeIds - IDs of entities that were just dragged.
 * @param ctx - The current context to read entity positions from. Pass txManager.getContext()
 *   when inside a deferred transaction so that the most recent (uncommitted) positions are used.
 */
export function dropNodesIntoGroup(
  store: CanvasStore,
  draggedNodeIds: string[],
  ctx: CanvasStoreContext,
): void {
  if (draggedNodeIds.length === 0) return;

  const entities = [
    ...Object.values(ctx.nodes),
    ...Object.values(ctx.connectors),
    ...Object.values(ctx.groups),
  ];

  // Build entity lookup map (O(1) access instead of O(n) entities.find)
  const entityById = new Map(entities.map(e => [e.id, e]));

  // Build rect map from all non-connector, non-group entities
  const memberRectMap = new Map<string, { x: number; y: number; width: number; height: number }>();
  for (const e of entities) {
    if (!getHandler(e.kind).hasOwnRect) continue;
    memberRectMap.set(e.id, { x: e.x, y: e.y, width: e.width, height: e.height });
  }

  // Build groups from group entities
  const freshGroups: CanvasGroup[] = [];
  for (const e of entities) {
    if (e.kind !== 'group') continue;
    const gd = e.data as unknown as GroupData;
    const memberNodeIds: string[] = [];
    const memberGroupIds: string[] = [];
    for (const mid of gd.memberEntityIds) {
      const member = entityById.get(mid);
      if (member?.kind === 'group') memberGroupIds.push(mid);
      else memberNodeIds.push(mid);
    }
    freshGroups.push({
      id: e.id,
      label: gd.label,
      description: gd.description,
      backgroundColor: gd.backgroundColor,
      collapsed: gd.collapsed,
      collapsedX: gd.collapsedX,
      collapsedY: gd.collapsedY,
      memberEntityIds: gd.memberEntityIds,
      memberNodeIds,
      memberGroupIds,
      layerId: e.layerId,
      displayMode: gd.displayMode ?? 'invisible',
    });
  }

  if (freshGroups.length === 0) return;

  // Compute fresh bounds
  const boundsMap = buildGroupBoundsMap(freshGroups, memberRectMap);

  // Build membership: nodeId -> group IDs it already belongs to
  const memberOf = new Map<string, Set<string>>();
  for (const g of freshGroups) {
    for (const nid of g.memberNodeIds) {
      if (!memberOf.has(nid)) memberOf.set(nid, new Set());
      memberOf.get(nid)!.add(g.id);
    }
  }

  // Build layer maps for cross-layer protection
  const entityLayerMap = new Map(entities.map(e => [e.id, e.layerId]));
  const groupLayerMap = new Map(freshGroups.map(g => [g.id, g.layerId]));

  // For each dragged entity: find smallest containing group it's not already in
  const additions = new Map<string, string[]>();
  for (const nodeId of draggedNodeIds) {
    const rect = memberRectMap.get(nodeId);
    if (!rect) continue;
    const cx = rect.x + rect.width / 2;
    const cy = rect.y + rect.height / 2;
    const nodeGroups = memberOf.get(nodeId);
    const nodeLayer = entityLayerMap.get(nodeId);

    let bestGroupId: string | null = null;
    let bestArea = Infinity;
    for (const [gId, bounds] of boundsMap) {
      const groupLayer = groupLayerMap.get(gId);
      if (groupLayer !== nodeLayer) continue;
      if (cx < bounds.x || cx > bounds.x + bounds.width) continue;
      if (cy < bounds.y || cy > bounds.y + bounds.height) continue;
      if (nodeGroups?.has(gId)) continue;
      const area = bounds.width * bounds.height;
      if (area < bestArea) { bestArea = area; bestGroupId = gId; }
    }

    if (bestGroupId) {
      if (!additions.has(bestGroupId)) additions.set(bestGroupId, []);
      additions.get(bestGroupId)!.push(nodeId);
    }
  }

  for (const [groupId, nodeIds] of additions) {
    const group = freshGroups.find(g => g.id === groupId);
    if (!group) continue;
    store.send({
      type: 'group.setMembers',
      groupId,
      memberEntityIds: [...group.memberNodeIds, ...nodeIds, ...group.memberGroupIds],
    });
  }
}
